import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import type { DataPointScoreEmployeeWidgetDto } from "../../../types/dtos/content-dashboard/ContentDashboardWidgetDto";
import type WidgetScoreDataDto from "../../../types/dtos/content-dashboard/WidgetScoreDataDto";
import Popover from "../../common/Popover";
import SafeRenderHtml from "../../common/SafeRenderHtml";
import Tooltip from "../../common/Tooltip";
import HalfDoughnutChart from "../common/HalfDoughnutChart";
import FullDoughnutChart from "../common/FullDoughnutChart";
import LinearGaugeChart from "../common/LinearGaugeChart";
import ScoreOnlyDisplay from "../common/ScoreOnlyDisplay";
import Sparkline from "../common/Sparkline";
import SubScoresBreakdown from "../common/SubScoresBreakdown";

interface DataPointScoreEmployeeWidgetCardProps {
  widget: DataPointScoreEmployeeWidgetDto;
}

const DataPointScoreEmployeeWidgetCard: React.FC<
  DataPointScoreEmployeeWidgetCardProps
> = ({ widget }) => {
  const [isHelpPopoverOpen, setIsHelpPopoverOpen] = useState(false);
  // Track which score is selected (0 = latest/current)
  const [selectedScoreIndex, setSelectedScoreIndex] = useState(0);
  // Track if breakdown is expanded (for flip animation)
  const [isExpanded, setIsExpanded] = useState(false);

  if (!widget.currentScore) return null;

  // Determine if we should show trend (need showTrendOverTime AND more than 1 score)
  const shouldShowTrend =
    widget.showTrendOverTime && widget.trendScores.length > 1;

  // Get the score to display based on selection
  const getSelectedScore = (): WidgetScoreDataDto => {
    if (shouldShowTrend && widget.trendScores[selectedScoreIndex]) {
      return widget.trendScores[selectedScoreIndex];
    }
    return widget.currentScore!;
  };

  // Get chart data based on whether we're using scale or percentage
  const getChartData = () => {
    const score = getSelectedScore();
    if (isNaN(Number(score.displayValue))) {
      return {
        numericValue: undefined,
        minValue: undefined,
        maxValue: undefined,
        displayText: score.displayValue,
        label: undefined,
      };
    }

    if (
      score.useScale &&
      score.scaleScore !== undefined &&
      score.scaleScore !== null &&
      score.scaleMax !== null &&
      score.scaleMax !== undefined
    ) {
      // Use scale score
      const isInteger = score.scaleScore === Math.floor(score.scaleScore);
      const formatted = isInteger
        ? score.scaleScore.toFixed(0)
        : score.scaleScore.toFixed(1);

      return {
        numericValue: score.scaleScore,
        minValue: score.scaleMin ?? 0,
        maxValue: score.scaleMax,
        displayText: formatted,
        label: `out of ${score.scaleMax}`,
      };
    }

    // Use percentage
    return {
      numericValue: Number(score.displayValue),
      minValue: 0,
      maxValue: 100,
      displayText: `${Number(score.displayValue).toFixed(0)}%`,
      label: undefined,
    };
  };

  const chartData = getChartData();
  const hasSubScores =
    getSelectedScore().subScores && getSelectedScore().subScores!.length > 0;

  // Format date for display (e.g., "Nov '25")
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear().toString().slice(-2);
    return `${month} '${year}`;
  };

  // Format full date for tooltip (e.g., "November 24, 2025")
  const formatFullDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  // Render the appropriate chart based on chartType
  const renderChart = () => {
    const commonProps = {
      value: chartData.numericValue,
      minValue: chartData.minValue,
      maxValue: chartData.maxValue,
      color: widget.color,
      displayText: chartData.displayText,
      label: chartData.label,
    };

    if (commonProps.value === undefined || commonProps.maxValue === undefined) {
      // Non-numeric, or max value is undefined (which is needed for numeric charts like gauges, donuts, etc.)
      return (
        <ScoreOnlyDisplay
          color={widget.color}
          displayText={chartData.displayText}
          label={chartData.label}
        />
      );
    } else {
      // Numeric
      switch (widget.chartType) {
        case "HALF-DOUGHNUT":
          return (
            <HalfDoughnutChart
              value={commonProps.value}
              maxValue={commonProps.maxValue}
              color={widget.color}
              displayText={chartData.displayText}
              label={chartData.label}
            />
          );
        case "FULL-DOUGHNUT":
          return (
            <FullDoughnutChart
              value={commonProps.value}
              maxValue={commonProps.maxValue}
              color={widget.color}
              displayText={chartData.displayText}
              label={chartData.label}
              hasSparkline={shouldShowTrend}
            />
          );
        case "LINEAR-GAUGE":
          return (
            <LinearGaugeChart
              value={commonProps.value}
              maxValue={commonProps.maxValue}
              color={widget.color}
              displayText={chartData.displayText}
            />
          );
        case "SCORE-ONLY":
          return (
            <ScoreOnlyDisplay
              color={widget.color}
              displayText={chartData.displayText}
              label={chartData.label}
            />
          );
        default:
          return (
            <HalfDoughnutChart
              value={commonProps.value}
              maxValue={commonProps.maxValue}
              color={widget.color}
              displayText={chartData.displayText}
              label={chartData.label}
            />
          );
      }
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 !pb-0 shadow-sm h-full flex flex-col">
      {/* Widget Title with Help Text */}
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <Tooltip
          content={widget.title}
          appearAt="top"
          triggerElement={
            <p className="font-semibold text-gray-900 flex-grow truncate cursor-default">
              {widget.title}
            </p>
          }
        />
        {widget.helpText && (
          <Popover
            triggerElement={
              <button className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none">
                <FontAwesomeIcon icon={faCircleInfo} size="sm" />
              </button>
            }
            openState={isHelpPopoverOpen}
            onOpenStateChange={setIsHelpPopoverOpen}
            placement="top"
            showCloseIcon={true}
            darkMode={true}
          >
            <SafeRenderHtml
              htmlText={widget.helpText}
              containerClassName="text-sm text-white leading-relaxed"
            />
          </Popover>
        )}
      </div>

      {/* 3D Flip Container with Perspective - Grows to fill space */}
      <div className="flex-1 min-h-0" style={{ perspective: "1000px" }}>
        <div
          className="h-full"
          style={{
            position: "relative",
            transformStyle: "preserve-3d",
            transition: "transform 0.6s",
            transform: isExpanded ? "rotateY(-180deg)" : "rotateY(0deg)",
          }}
        >
          {/* Front Face - Chart and Sparkline */}
          <div
            className="h-full flex flex-col"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
            }}
          >
            {/* Top Spacer - pushes content to center */}
            <div className="flex-grow" />

            {/* Chart Visualization */}
            <div className="pt-2 flex-shrink-0">{renderChart()}</div>

            {/* Trend Line (Sparkline) with Date Labels OR Date Indicator */}
            {shouldShowTrend ? (
              <div className="flex-shrink-0">
                <Sparkline
                  trendScores={widget.trendScores}
                  selectedIndex={selectedScoreIndex}
                  onPointClick={setSelectedScoreIndex}
                  color={widget.color}
                />
              </div>
            ) : (
              <div className="flex-shrink-0 py-2">
                <Tooltip
                  content={formatFullDate(getSelectedScore().scoreDate)}
                  appearAt="top"
                  triggerElement={
                    <div className="text-center text-xs text-gray-900 font-semibold cursor-default">
                      {formatDate(getSelectedScore().scoreDate)}
                    </div>
                  }
                />
              </div>
            )}

            {/* Bottom Spacer - creates equal space on both sides */}
            <div className="flex-grow" />

            {/* Show Breakdown Button */}
            {hasSubScores && (
              <div className="border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setIsExpanded(true)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 transition-all duration-200 group"
                >
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                    Show breakdown
                  </span>
                </button>
              </div>
            )}

            {/* Fixed spacer for Score Only chart type without breakdown to maintain alignment */}
            {!hasSubScores && widget.chartType === "SCORE-ONLY" && (
              <div className="h-[30px] flex-shrink-0" />
            )}
          </div>

          {/* Back Face - Sub-Scores Breakdown */}
          {hasSubScores && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                transform: "rotateY(-180deg)",
              }}
            >
              <SubScoresBreakdown
                key={selectedScoreIndex}
                subScores={getSelectedScore().subScores || []}
                color={widget.color}
                onToggle={() => setIsExpanded(false)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataPointScoreEmployeeWidgetCard;
